import { lazy, Suspense, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { skipToken } from '@reduxjs/toolkit/query';
import { useAppDispatch, useAppSelector } from '@/app/hooks';
import { useListEventsQuery } from './eventsApi';
import {
  overlapsWantedAges,
  selectAppliedQuery,
  selectFilters,
  selectFocusPoint,
  sortChanged,
  withinPrice,
} from './filtersSlice';
import { availableSorts, SORT_LABELS, sortRows, type SortOrder } from './sortRows';
import { Pagination } from './Pagination';
import { EventTimetable } from './EventTimetable';
import { LocationPrompt } from './LocationPrompt';
import { EventFilters } from './EventFilters';
import { useMediaQuery, WIDE_VIEWPORT } from '@/app/useMediaQuery';
import { deriveKeywords, matchesKeywords } from './keywords';
import { AlertPrompt } from '@/features/subscriptions/AlertPrompt';
import { OrganiserPrompt } from './OrganiserPrompt';
import { CityList, OverviewHeading, UpcomingEvents } from './HomeOverview';
import { originFor, useOverview } from './useOverview';
import { resultsHeading, useSearchNavigation, useUrlDrivenSearch } from './searchUrl';
import { Callout } from '@mmef/shared/components';
import { messageOf } from '@mmef/shared/api';
import { coordinatesOf, distanceKm } from '@mmef/shared/lib';
import type { CityCount, EventSummary } from '@mmef/shared/api';

// Leaflet and its CSS are ~150 kB that nothing else in the app needs, and the pages behind
// sign-in never show a map. Split it out and let the panel fill in a moment later.
const EventsMap = lazy(() => import('./EventsMap').then((m) => ({ default: m.EventsMap })));

/** Above this many results, the count follows you down the page instead of scrolling away. */
const STICKY_COUNT_THRESHOLD = 20;

/** Rows per page. Ten is about a screen of timetable without scrolling past the map beside it. */
const PAGE_SIZE = 10;

type View = 'map' | 'list';

interface Placed {
  event: EventSummary;
  /** null when the search had no origin, or the event has no coordinates. */
  distance: number | null;
}

export function EventsPage() {
  const { where } = useUrlDrivenSearch();
  const { search, backToOverview } = useSearchNavigation();
  const overview = useOverview();
  const applied = useAppSelector(selectAppliedQuery);
  const focus = useAppSelector(selectFocusPoint);
  const dispatch = useAppDispatch();
  const { keywords, ageFrom, ageTo, origin, maxPrice, freeOnly, sort } = useAppSelector(selectFilters);
  // Wide screens show both at once; narrow ones tab between them. This is a JS check rather than
  // pure CSS because it decides whether the map is *mounted* — hiding it with CSS would still
  // download Leaflet on a phone, which is the cost we're avoiding.
  const isWide = useMediaQuery(WIDE_VIEWPORT);
  const [view, setView] = useState<View>('list');
  // Which event the pointer is over, in either panel. Shared so hovering a row lights its pin.
  const [activeId, setActiveId] = useState<number | null>(null);
  /*
   * The row picked in the results list, so the map can fly to it.
   *
   * Kept apart from activeId on purpose: hovering a row highlights its pin, clicking one moves
   * the map. Driving the map from hover would send it lurching every time the pointer crossed
   * the table, which makes the map unreadable exactly while you are trying to read it.
   */
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [page, setPage] = useState(1);

  // skipToken, not `{ skip }`: it keeps the hook's argument type honest, so there's no fake query
  // object standing in for "haven't searched yet".
  const { data, isLoading, isFetching, isError, error, refetch } = useListEventsQuery(applied ?? skipToken);

  /*
   * Everything upcoming, for the map.
   *
   * <p>The map's job is to show what is out there, not what was last searched — so it is given
   * every event and decides what to draw from how far out the reader is. Three things make this
   * cheap: it is one request, it is the same query a national search makes (so the two share a
   * cache entry rather than fetching twice), and it is skipped entirely until the map is mounted,
   * which on a phone means not until someone chooses the Map tab.
   *
   * <p>If the catalogue ever outgrows a single response, this is the line that has to become a
   * query by map bounds. Nothing else about the map would change.
   */
  const mapMounted = isWide || view === 'map';
  const { data: allEvents } = useListEventsQuery(mapMounted ? { country: 'GB' } : skipToken);

  // Suggestions come from everything fetched, not from what survives filtering — otherwise chips
  // would disappear as you picked them and there'd be no way back.
  const suggestions = useMemo(() => deriveKeywords(data ?? []), [data]);

  // Filtered here rather than server-side: the API has no text parameter, and one country's worth
  // of results is small enough that a round trip per click would be the worse trade.
  const events = useMemo(
    () =>
      (data ?? [])
        .filter((event) => matchesKeywords(event, keywords))
        .filter((event) => withinPrice(event.price, maxPrice, freeOnly)),
    [data, keywords, maxPrice, freeOnly],
  );

  /**
   * The list, measured then ordered.
   *
   * <p>Measuring is separate from ordering because distance is displayed whether or not it's the
   * sort key — the "away" column needs it regardless.
   *
   * <p>Which orderings are on offer depends on the data: distance needs an origin, price needs at
   * least one event that states one. If the chosen sort loses its data — clearing the location
   * while sorted by distance — it falls back rather than silently doing nothing.
   */
  const placed = useMemo<Placed[]>(
    () =>
      events.map((event) => {
        const point = coordinatesOf(event.latitude, event.longitude);
        return {
          event,
          distance: origin && point ? distanceKm([origin.lat, origin.lng], point) : null,
        };
      }),
    [events, origin],
  );

  const sortOptions = useMemo(() => availableSorts(placed, Boolean(origin)), [placed, origin]);
  // Until they choose, answer the question they asked: a search near somewhere is a distance
  // question, a search across the country is a "what's next" one.
  const chosenSort = sort ?? (origin ? 'nearest' : 'soonest');
  const activeSort = sortOptions.includes(chosenSort) ? chosenSort : 'soonest';
  const ordered = useMemo(() => sortRows(placed, activeSort), [placed, activeSort]);

  // The address bar, not the store, says whether this is a search: on a refresh the URL is there
  // before the store has caught up, and the overview must not flash in first.
  const hasSearched = where !== null;
  const unmappable = placed.filter((p) => p.distance === null).length;

  /*
   * What the distances are measured from, said once above the results.
   *
   * "you" once the reader has shared their location, otherwise the town they picked — which is a
   * fixed point that may be nowhere near them. It was a column heading until the columns went;
   * before that it said "Away" regardless, which reads as "away from you" and was true in only one
   * of those two cases.
   */
  const distanceFrom = origin ? (origin.source === 'geo' ? 'you' : origin.label) : undefined;

  /**
   * Splits results by age, when an age was given.
   *
   * <p>Two lists rather than one, because "no age stated" and "matches your age" are genuinely
   * different answers and merging them would either hide events wrongly or imply a match nobody
   * claimed. Most events come from linked accounts that carry no age data at all, so the second
   * list is often the bigger one — and saying so is more honest than quietly dropping it.
   */
  const { matching, ageUnknown } = useMemo(() => {
    const from = Number.parseInt(ageFrom, 10);
    const to = Number.parseInt(ageTo, 10);
    const wantedMin = Number.isFinite(from) ? from : null;
    const wantedMax = Number.isFinite(to) ? to : null;

    // Split the ordered list, not the raw one — filtering preserves order, so each half stays in
    // the sort the reader chose.
    if (wantedMin === null && wantedMax === null) {
      return { matching: ordered, ageUnknown: [] as Placed[] };
    }
    return {
      matching: ordered.filter(
        ({ event }) =>
          event.ageRanges.length > 0 && overlapsWantedAges(event.ageRanges, wantedMin, wantedMax),
      ),
      ageUnknown: ordered.filter(({ event }) => event.ageRanges.length === 0),
    };
  }, [ordered, ageFrom, ageTo]);

  const shown = useMemo(() => [...matching, ...ageUnknown], [matching, ageUnknown]);

  /**
   * One page of results.
   *
   * <p>Paginated here rather than in the query, because filtering happens here: the age and price
   * filters run over loaded results, so a server returning ten rows would hand back a page this
   * code then cuts to three, with nothing to refill it from. Sorting, filtering and pagination
   * have to live on the same side of the wire — see {@link sortRows} for when that side changes.
   *
   * <p>The page is clamped rather than trusted: narrowing a filter can shrink the results under
   * someone standing on page four.
   */
  const pageCount = Math.max(1, Math.ceil(shown.length / PAGE_SIZE));
  const safePage = Math.min(page, pageCount);
  const pageRows = shown.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  // The page is sliced across both groups, then split back into them, so a page is always ten
  // events rather than ten-plus-however-many-unknowns.
  const unknownIds = useMemo(() => new Set(ageUnknown.map(({ event }) => event.id)), [ageUnknown]);
  const pageMatching = pageRows.filter(({ event }) => !unknownIds.has(event.id));
  const pageUnknown = pageRows.filter(({ event }) => unknownIds.has(event.id));

  // A new search or a different order is a different result set; staying on page four of it would
  // land someone in the middle of something they have never seen.
  useEffect(() => {
    setPage(1);
  }, [applied, activeSort, keywords, ageFrom, ageTo, maxPrice, freeOnly]);

  const pickCity = (city: CityCount) => {
    const origin = originFor(city);
    if (!origin) return;
    search(origin);
    revealResults();
  };

  const seeAll = () => {
    search(null);
    revealResults();
  };

  /*
   * The fade only belongs to a change the reader caused. On first load the server has already drawn
   * this page, and React replacing it with a fading copy would blink the content out and back in.
   */
  const [initialWhere] = useState(where);
  const [hasTransitioned, setHasTransitioned] = useState(false);
  useEffect(() => {
    if (where !== initialWhere) setHasTransitioned(true);
  }, [where, initialWhere]);
  const swap = hasTransitioned || where !== initialWhere ? ' page-swap' : '';

  const mapCities = overview ? [...overview.cities, ...overview.otherCities] : undefined;
  const loadingResults = hasSearched && (applied === null || isLoading);

  /*
   * What the map draws, and which of it answers the search.
   *
   * <p>Before the full set arrives the results are all there is, which is better than an empty
   * map for the second it takes. The ids are what let the map fade the rest of the country
   * instead of hiding it.
   */
  const mapEvents = useMemo(
    () => allEvents ?? (hasSearched ? shown.map(({ event }) => event) : []),
    [allEvents, hasSearched, shown],
  );
  const highlightIds = useMemo(
    () => (hasSearched ? new Set(shown.map(({ event }) => event.id)) : null),
    [hasSearched, shown],
  );

  return (
    <div className="page">
      {/* First in both states, so it never moves: picking a city changes everything beneath the
          search bar and nothing above it. */}
      <div id="search" className="search-anchor">
        <EventFilters isSearching={isFetching} suggestions={suggestions} />
      </div>

      {/* Keyed on what's being shown, so each change remounts and fades in rather than snapping. */}
      <div className={swap.trim() || undefined} key={hasSearched ? `head-${where}` : 'head-overview'}>
        {hasSearched ? (
          <div className="page-head">
            <button type="button" className="back-link" onClick={backToOverview}>
              <span aria-hidden="true">←</span> All events
            </button>
            <h1 className="page-head__title">{resultsHeading(where, origin?.label)}</h1>
          </div>
        ) : (
          <OverviewHeading />
        )}
      </div>

      {/* The homepage only. Once someone has picked somewhere, the page goes straight to events.
          Both cards can vanish independently, so the grid handles one-of-two and none-of-two. */}
      {!hasSearched && (
        <div className="promo-pair">
          <AlertPrompt />
          <OrganiserPrompt />
        </div>
      )}

      {/* Only once there are results to improve. Offering to fix distances before any have been
          shown is asking for a permission with nothing yet to spend it on. */}
      {hasSearched && <LocationPrompt />}

      {hasSearched && isError && (
        <Callout tone="error" title="Couldn't load events">
          {messageOf(error)}{' '}
          <button type="button" className="button button--link" onClick={() => refetch()}>
            Try again
          </button>
        </Callout>
      )}

      {hasSearched && !loadingResults && data && events.length === 0 && (
        <Callout tone="info" title="Nothing here yet">
          No upcoming events match those filters. Try widening the search, or{' '}
          <Link to="/submit">submit one you know about</Link>.
        </Callout>
      )}

      {hasSearched && events.length > 0 && (
        <div className={`results-bar${events.length > STICKY_COUNT_THRESHOLD ? ' results-bar--sticky' : ''}`}>
          <p className="results-count" aria-live="polite">
            {shown.length} event{shown.length === 1 ? '' : 's'}
            {/* Where the distances are measured from. This was a column heading — "From Bradford" —
                and the column went when the table did, but the fact still has to be said somewhere:
                a distance from a town centre is not a distance from the reader. */}
            {origin && distanceFrom && ` · distances from ${distanceFrom}`}
            {ageUnknown.length > 0 && ` · ${matching.length} match the ages you asked for`}
            {/* isFetching without isLoading means a background refresh over stale data. */}
            {isFetching && !isLoading && ' · refreshing…'}
          </p>

          {/* Only offered when there's more than one way to order — a one-option select is
              furniture pretending to be a control. */}
          {sortOptions.length > 1 && (
            <label className="results-sort">
              <span className="visually-hidden">Sort results by</span>
              <select value={activeSort} onChange={(event) => dispatch(sortChanged(event.target.value as SortOrder))}>
                {sortOptions.map((option) => (
                  <option key={option} value={option}>
                    {SORT_LABELS[option]}
                  </option>
                ))}
              </select>
            </label>
          )}
        </div>
      )}

      {/* Only a choice when there isn't room for both, and the same choice before and after a
          search. On a wide screen the tabs would be asking you to hide half of what already fits. */}
      {!isWide && (
        <div className="tabs tabs--compact view-tabs" role="tablist" aria-label="Result view">
          {(['list', 'map'] as const).map((option) => (
            <button
              key={option}
              type="button"
              role="tab"
              aria-selected={view === option}
              className={`tab${view === option ? ' tab--active' : ''}`}
              onClick={() => setView(option)}
            >
              {option === 'map' ? 'Map' : 'List'}
            </button>
          ))}
        </div>
      )}

      <div className={`results${isWide ? ' results--split' : ''}`}>
        {(isWide || view === 'list') && (
          <div className={`results__list${swap}`} key={hasSearched ? 'list-results' : 'list-overview'}>
            {!hasSearched && <CityList onPick={pickCity} />}

            {loadingResults && <ResultsSkeleton />}

            {hasSearched && !loadingResults && events.length > 0 && (
              <>
                {origin && unmappable > 0 && (
                  <p className="field__hint">
                    {unmappable} event{unmappable === 1 ? ' has' : 's have'} no recorded location, so{' '}
                    {unmappable === 1 ? "it's" : "they're"} listed last.
                  </p>
                )}

                {pageMatching.length > 0 && (
                  <EventTimetable
                    rows={pageMatching}
                    onSelect={setSelectedId}
                    selectedId={selectedId}
                    activeId={activeId}
                    onHover={setActiveId}
                    showDistance={Boolean(origin)}
                    caption="Events matching the ages you asked for"
                  />
                )}

                {pageUnknown.length > 0 && (
                  <section className="section">
                    <h2 className="results-secondary">
                      {ageUnknown.length} more in your area, but we don&apos;t know who they&apos;re for
                    </h2>
                    <p className="field__hint">
                      These match everything else you asked for. The organiser hasn&apos;t said which ages
                      they&apos;re aimed at, so they might or might not suit you — it&apos;s worth checking the
                      event itself.
                    </p>
                    <EventTimetable
                      rows={pageUnknown}
                      onSelect={setSelectedId}
                      selectedId={selectedId}
                      activeId={activeId}
                      onHover={setActiveId}
                      showDistance={Boolean(origin)}
                      caption="Events with no stated age range"
                    />
                  </section>
                )}

                <Pagination page={safePage} pageCount={pageCount} onChange={setPage} />
              </>
            )}
          </div>
        )}

        {/* One map for both states, never keyed: it stays mounted when a city is picked and flies
            from the country's count bubbles to that city's events. Behind a tab on a phone, it
            stays unmounted — and Leaflet undownloaded — until someone asks for it. */}
        {(isWide || view === 'map') && (
          <div className="results__map">
            {/* The loading box sits inside the map's frame, as the map will, so its border is counted
                and nothing below moves by a couple of pixels when the map arrives. */}
            <Suspense
              fallback={
                <div className="map-panel">
                  <div className="map-panel--loading" />
                </div>
              }
            >
              {/* Every event, and every result — not just this page: the map answers "where is
                  any of this", which paginating would break. Both age groups too, or areas go
                  blank unexplained. The cities stay on it during a search as well as before one,
                  so zooming out of a city shows the rest of the country rather than empty sea. */}
              <EventsMap
                events={mapEvents}
                highlightIds={highlightIds}
                focus={hasSearched ? focus : null}
                radiusMeters={hasSearched ? applied?.radiusMeters : null}
                activeId={activeId}
                onHover={setActiveId}
                selectedId={selectedId}
                cities={mapCities}
                onSearchCity={pickCity}
                searchedPlace={hasSearched ? (origin?.label ?? null) : null}
              />
            </Suspense>
          </div>
        )}
      </div>

      {!hasSearched && (isWide || view === 'list') && <UpcomingEvents onSeeAll={seeAll} />}
    </div>
  );
}

/**
 * Where results will be, while they load.
 *
 * <p>Rows the shape of the timetable rather than a spinner: the page keeps its layout, so the real
 * rows fade in where the placeholders were instead of pushing everything around when they arrive.
 */
function ResultsSkeleton() {
  return (
    <div className="skeleton" role="status">
      <span className="visually-hidden">Loading events</span>
      {[0, 1, 2, 3].map((row) => (
        <div className="skeleton__row" key={row} aria-hidden="true">
          <span className="skeleton__block skeleton__block--date" />
          <span className="skeleton__lines">
            <span className="skeleton__block" />
            <span className="skeleton__block skeleton__block--short" />
          </span>
        </div>
      ))}
    </div>
  );
}

/**
 * Brings the results into view after picking something far down the page — a map bubble, or
 * "See all" under the upcoming list. Only if the search bar has scrolled away; otherwise the page
 * stays exactly where it is, which is the point.
 */
function revealResults() {
  const anchor = document.getElementById('search');
  if (!anchor || anchor.getBoundingClientRect().top >= 0) return;
  const reduced =
    typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  anchor.scrollIntoView?.({ behavior: reduced ? 'auto' : 'smooth', block: 'start' });
}
